
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Store {
  id: string;
  name: string;
  address: string;
  phone?: string | null;
  email?: string | null;
  is_active?: boolean | null;
  coordinates?: {
    latitude: number;
    longitude: number;
  } | null;
  distance_km?: number;
  distance?: number;
  duration_minutes?: number;
  deliveryTime?: number;
  eta?: number;
  rating?: number;
  reviews?: number;
  hours?: string;
  image?: string;
  product_count?: number;
}

type Coordinates = {
  latitude: number | null;
  longitude: number | null;
};

type LocationContextType = {
  coordinates: Coordinates;
  loading: boolean;
  isLoading: boolean;
  error: string | null;
  updateCoordinates: (lat: number, lng: number) => void;
  requestLocation: () => Promise<void>;
  currentAddress: string;
  deliveryTimeFormatted: string;
  nearestStore: Store | null;
  isLoadingStore: boolean;
  refreshLocation: () => Promise<void>;
  selectStore: (store: Store) => void;
  isLocationSet: boolean;
  findNearestStore: (lat: number, lng: number) => Promise<Store | null>;
  setCurrentAddress: (address: string) => void;
  hasManuallySelectedAddress: boolean;
  clearStore: () => void;
  isServiceable: boolean;
  selectAddress: (addressString: string, addressObj?: {
    id: string;
    name: string;
    address: string;
    latitude?: number;
    longitude?: number;
  }) => Promise<void>;
};

const defaultCoordinates: Coordinates = {
  latitude: null,
  longitude: null,
};

const LocationContext = createContext<LocationContextType>({
  coordinates: defaultCoordinates,
  loading: false,
  isLoading: false,
  error: null,
  updateCoordinates: () => {},
  requestLocation: async () => {},
  currentAddress: 'Locating your address...',
  deliveryTimeFormatted: '30-40 min',
  nearestStore: null,
  isLoadingStore: false,
  refreshLocation: async () => {},
  selectStore: () => {},
  isLocationSet: false,
  findNearestStore: async () => null,
  setCurrentAddress: () => {},
  hasManuallySelectedAddress: false,
  clearStore: () => {},
  isServiceable: false,
  selectAddress: async () => {},
});

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [coordinates, setCoordinates] = useState<Coordinates>(defaultCoordinates);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAddress, setCurrentAddress] = useState<string>('Locating your address...');
  const [nearestStore, setNearestStore] = useState<Store | null>(null);
  const [isLoadingStore, setIsLoadingStore] = useState<boolean>(false);
  const [deliveryTimeMinutes, setDeliveryTimeMinutes] = useState<number | null>(null);
  const [hasManuallySelectedAddress, setHasManuallySelectedAddress] = useState<boolean>(false);
  const [isServiceable, setIsServiceable] = useState<boolean>(false);
  const [isUsingIpLocation, setIsUsingIpLocation] = useState<boolean>(false);
  const [addressResolutionPending, setAddressResolutionPending] = useState<boolean>(false);
  const { toast } = useToast();

  const deliveryTimeFormatted = deliveryTimeMinutes 
    ? deliveryTimeMinutes < 60 
      ? `${deliveryTimeMinutes} mins` 
      : `${Math.floor(deliveryTimeMinutes / 60)} hr ${deliveryTimeMinutes % 60 > 0 ? `${deliveryTimeMinutes % 60} mins` : ''}`
    : '30-40 min';

  const formatAddress = (addressComponents: google.maps.GeocoderAddressComponent[]): string => {
    let formattedAddress = '';
    
    const premise = addressComponents.find(c => c.types.includes('premise'))?.long_name;
    const streetNumber = addressComponents.find(c => c.types.includes('street_number'))?.long_name;
    const sublocality = addressComponents.find(c => c.types.includes('sublocality_level_1'))?.long_name;
    const locality = addressComponents.find(c => c.types.includes('locality'))?.long_name;
    const route = addressComponents.find(c => c.types.includes('route'))?.long_name;
    
    if (premise) formattedAddress += premise + ', ';
    else if (streetNumber) formattedAddress += streetNumber + ' ';
    if (route) formattedAddress += route + ', ';
    if (sublocality) formattedAddress += sublocality + ', ';
    if (locality) formattedAddress += locality;
    
    return formattedAddress || 'Unknown location';
  };

  const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string> => {
    if (!lat || !lng) {
      return 'Location not available';
    }
    
    setAddressResolutionPending(true);
    
    try {
      // Check if Google Maps API is loaded
      if (!window.google?.maps?.Geocoder) {
        console.log('Google Maps Geocoder not loaded yet, retrying in 2 seconds...');
        return new Promise((resolve) => {
          setTimeout(async () => {
            if (window.google?.maps?.Geocoder) {
              const address = await getAddressFromCoordinates(lat, lng);
              resolve(address);
            } else {
              console.log('Google Maps Geocoder still not available after retry');
              resolve(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            }
          }, 2000);
        });
      }
      
      const geocoder = new window.google.maps.Geocoder();
      
      return new Promise<string>((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          setAddressResolutionPending(false);
          
          if (status === "OK" && results && results.length > 0) {
            const result = results[0];
            if (result.address_components) {
              const formattedAddr = formatAddress(result.address_components);
              console.log('Resolved address from coordinates:', formattedAddr);
              resolve(formattedAddr);
            } else {
              console.log('Using formatted address:', result.formatted_address);
              resolve(result.formatted_address || 'Address found but format not available');
            }
          } else {
            console.warn(`Geocoder failed with status: ${status}`);
            resolve(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        });
      });
    } catch (err) {
      console.error('Error in reverse geocoding:', err);
      setAddressResolutionPending(false);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  const findNearestStore = async (lat: number, lng: number): Promise<Store | null> => {
    setIsLoadingStore(true);
    setIsServiceable(false);
    
    try {
      console.log(`Finding nearest store for coordinates: ${lat}, ${lng}`);
      const { data, error } = await supabase.functions.invoke('store-locator', {
        body: {
          latitude: lat,
          longitude: lng,
          radius: 5
        }
      });
      
      if (error) {
        throw error;
      }
      
      console.log('Store locator response:', data);
      
      if (data && data.stores && data.stores.length > 0) {
        const nearest = data.stores.reduce((prev: Store, current: Store) => {
          return (prev.distance_km || Infinity) < (current.distance_km || Infinity) ? prev : current;
        });
        
        const deliveryMins = Math.round((nearest.distance_km || 0) * 3) + 15;
        setDeliveryTimeMinutes(deliveryMins);
        
        console.log('Selected nearest store:', nearest);
        setIsServiceable(true);
        setNearestStore(nearest);
        return nearest;
      }
      
      console.log('No nearby stores found - clearing store selection');
      setIsServiceable(false);
      setNearestStore(null);
      return null;
    } catch (err) {
      console.error('Error finding nearest store:', err);
      setError('Failed to locate nearby stores');
      setIsServiceable(false);
      setNearestStore(null);
      return null;
    } finally {
      setIsLoadingStore(false);
    }
  };

  const clearStore = () => {
    setNearestStore(null);
    setDeliveryTimeMinutes(null);
    setIsServiceable(false);
  };

  const updateCoordinates = async (latitude: number, longitude: number) => {
    console.log(`Updating coordinates: ${latitude}, ${longitude}`);
    setCoordinates({ latitude, longitude });
    setError(null);
    
    try {
      sessionStorage.setItem('instamg-coordinates', JSON.stringify({ 
        latitude, 
        longitude,
        isIpBased: isUsingIpLocation
      }));
    } catch (err) {
      console.error('Failed to save coordinates to session storage:', err);
    }
    
    try {
      if (!hasManuallySelectedAddress) {
        setCurrentAddress('Getting your address...');
      }
      
      if (!hasManuallySelectedAddress) {
        const address = await getAddressFromCoordinates(latitude, longitude);
        console.log('Retrieved address from coordinates:', address);
        setCurrentAddress(address);
      }
      
      await findNearestStore(latitude, longitude);
    } catch (err) {
      console.error('Error updating location info:', err);
      if (!hasManuallySelectedAddress) {
        setCurrentAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      }
    }
  };

  const requestLocation = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setHasManuallySelectedAddress(false);
    setIsUsingIpLocation(false);
    setCurrentAddress('Locating your address...');

    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      // First try with high accuracy
      try {
        console.log('Trying to get precise location with high accuracy...');
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve, 
            reject, 
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            }
          );
        });

        const { latitude, longitude } = position.coords;
        console.log('Successfully got precise location:', { latitude, longitude });
        await updateCoordinates(latitude, longitude);
      } catch (highAccuracyError: any) {
        console.warn('High accuracy location failed, trying with low accuracy:', highAccuracyError);
        
        // If high accuracy fails, try with low accuracy
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              resolve, 
              reject, 
              {
                enableHighAccuracy: false,
                timeout: 10000,
                maximumAge: 60000,
              }
            );
          });
          
          const { latitude, longitude } = position.coords;
          console.log('Successfully got location with low accuracy:', { latitude, longitude });
          await updateCoordinates(latitude, longitude);
        } catch (lowAccuracyError: any) {
          console.error('Both high and low accuracy location attempts failed:', lowAccuracyError);
          
          let errorMessage = 'Unable to retrieve your location. ';
          
          if (highAccuracyError.code === 1 || lowAccuracyError.code === 1) {
            errorMessage += 'Please enable location services in your browser.';
          } else if (highAccuracyError.code === 2 || lowAccuracyError.code === 2) {
            errorMessage += 'Location information is unavailable.';
          } else if (highAccuracyError.code === 3 || lowAccuracyError.code === 3) {
            errorMessage += 'Location request timed out.';
          }
          
          setError(errorMessage);
          
          // Fall back to IP-based location
          tryIpBasedLocation();
        }
      }
    } catch (err: any) {
      console.error('Unexpected error getting location:', err);
      
      setError('An unexpected error occurred while trying to get your location.');
      
      // Fall back to IP-based location
      tryIpBasedLocation();
    } finally {
      setLoading(false);
    }
  };

  const tryIpBasedLocation = async () => {
    try {
      setIsUsingIpLocation(true);
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        console.log('Using IP-based location:', data);
        toast({
          title: "Using approximate location",
          description: "We're using an approximate location based on your IP address. For best results, please allow location access.",
          duration: 5000,
        });
        
        await updateCoordinates(data.latitude, data.longitude);
      } else {
        throw new Error('IP location data invalid');
      }
    } catch (err) {
      console.error('IP location fallback failed:', err);
      setIsUsingIpLocation(false);
      toast({
        title: "Location not available",
        description: "Please manually enter your delivery address for the best experience.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const refreshLocation = async (): Promise<void> => {
    await requestLocation();
  };

  const selectStore = (store: Store) => {
    setNearestStore(store);
    setIsServiceable(true);
    
    const deliveryMins = Math.round((store.distance_km || store.distance || 0) * 3) + 15;
    setDeliveryTimeMinutes(deliveryMins);
    
    // Dispatch event for UI components to react to store changes
    window.dispatchEvent(new CustomEvent('store-updated', { detail: store }));
    
    toast({
      title: "Store Selected",
      description: `${store.name} has been selected for your deliveries`,
      duration: 3000,
    });
  };

  const selectAddress = async (addressString: string, addressObj?: {
    id: string;
    name: string;
    address: string;
    latitude?: number;
    longitude?: number;
  }): Promise<void> => {
    setCurrentAddress(addressString);
    setHasManuallySelectedAddress(true);
    setIsUsingIpLocation(false);
    
    if (addressObj && addressObj.latitude && addressObj.longitude) {
      console.log(`LocationContext: Selecting address with coordinates:`, addressObj);
      
      clearStore();
      
      await updateCoordinates(addressObj.latitude, addressObj.longitude);
      
      const store = await findNearestStore(addressObj.latitude, addressObj.longitude);
      if (store) {
        console.log('Found nearest store for selected address:', store);
        setNearestStore(store);
        
        // Dispatch event for UI components to react to store changes
        window.dispatchEvent(new CustomEvent('store-updated', { detail: store }));
        
        toast({
          title: 'Delivery Address Updated',
          description: `Your medicines will be delivered from ${store.name}`,
          duration: 3000
        });
      } else {
        console.log('No store found for the selected address');
        setNearestStore(null);
        
        toast({
          title: 'Area Not Serviceable',
          description: `We don't have partner stores within 5km of this location yet.`,
          duration: 3000,
          variant: 'destructive'
        });
      }
    } else {
      console.log('Selected address does not have proper coordinates:', addressObj);
      
      clearStore();
      
      toast({
        title: 'Address Selected',
        description: `Please update this address to include location details for better delivery estimates.`,
        duration: 3000,
        variant: 'destructive'
      });
    }
    
    return Promise.resolve();
  };

  useEffect(() => {
    try {
      const savedCoordinates = sessionStorage.getItem('instamg-coordinates');
      if (savedCoordinates) {
        const parsed = JSON.parse(savedCoordinates);
        setCoordinates(parsed);
        
        if (parsed.isIpBased) {
          setIsUsingIpLocation(true);
        }
        
        if (!hasManuallySelectedAddress) {
          setCurrentAddress('Loading your saved location...');
        }
        
        (async () => {
          if (parsed.latitude && parsed.longitude) {
            try {
              if (!hasManuallySelectedAddress) {
                const address = await getAddressFromCoordinates(parsed.latitude, parsed.longitude);
                console.log('Loaded address from saved coordinates:', address);
                setCurrentAddress(address);
              }
              
              await findNearestStore(parsed.latitude, parsed.longitude);
            } catch (err) {
              console.error('Failed to get address from saved coordinates:', err);
              if (!hasManuallySelectedAddress) {
                setCurrentAddress(`${parsed.latitude.toFixed(6)}, ${parsed.longitude.toFixed(6)}`);
              }
            }
          }
        })();
      } else {
        console.log('No saved coordinates, requesting location');
        requestLocation();
      }
    } catch (err) {
      console.error('Failed to load coordinates from session storage:', err);
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{
        coordinates,
        loading,
        isLoading: loading,
        error,
        updateCoordinates,
        requestLocation,
        currentAddress,
        deliveryTimeFormatted,
        nearestStore,
        isLoadingStore,
        refreshLocation,
        selectStore,
        isLocationSet: coordinates.latitude !== null && coordinates.longitude !== null,
        findNearestStore,
        setCurrentAddress: (address) => {
          setCurrentAddress(address);
          setHasManuallySelectedAddress(true);
        },
        hasManuallySelectedAddress,
        clearStore,
        isServiceable,
        selectAddress,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
